import { useEffect, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { db } from '../db/db';
import { QRCanvas } from '../components/QRCanvas';
import { useWakeLock } from '../hooks/useWakeLock';
import { useAuth } from '../auth/AuthContext';
import { setSubjectStatus } from '../sync/progressSync';
import { useJobStatusSync } from '../hooks/useJobStatusSync';

const AUTO_MARK_ON_NEXT_KEY = 'qr-cards-auto-mark-on-next';

export function SubjectQRCardPage() {
  const { jobId, subjectId } = useParams<{ jobId: string; subjectId: string }>();
  const navigate = useNavigate();
  const { session } = useAuth();
  const [distractionFree, setDistractionFree] = useState(false);
  // Spec: "keep a child marked incomplete until the photographer explicitly
  // taps complete, or auto-mark when moving next." Default is the safer,
  // explicit-tap behaviour; this setting opts into the second one.
  const [autoMarkOnNext, setAutoMarkOnNext] = useState(() => localStorage.getItem(AUTO_MARK_ON_NEXT_KEY) === '1');

  useJobStatusSync(jobId);

  const subject = useLiveQuery(() => (subjectId ? db.subjects.get(subjectId) : undefined), [subjectId]);
  const groupSiblings = useLiveQuery(
    () => (subject ? db.subjects.where('groupId').equals(subject.groupId).sortBy('sourceOrder') : []),
    [subject?.groupId]
  );
  const group = useLiveQuery(() => (subject ? db.groups.get(subject.groupId) : undefined), [subject?.groupId]);

  useWakeLock(true);

  useEffect(() => {
    if (subjectId && jobId) sessionStorage.setItem(`qr-cards-last-viewed-${jobId}`, subjectId);
  }, [subjectId, jobId]);

  useEffect(() => {
    localStorage.setItem(AUTO_MARK_ON_NEXT_KEY, autoMarkOnNext ? '1' : '0');
  }, [autoMarkOnNext]);

  const index = useMemo(
    () => (groupSiblings && subjectId ? groupSiblings.findIndex(s => s.id === subjectId) : -1),
    [groupSiblings, subjectId]
  );
  const prev = groupSiblings && index > 0 ? groupSiblings[index - 1] : null;
  const next = groupSiblings && index >= 0 && index < groupSiblings.length - 1 ? groupSiblings[index + 1] : null;

  if (!subject || !group) return <div className="screen screen--centered">Loading…</div>;

  const blocked = !subject.qrPayloadOriginal;

  async function handleMarkComplete() {
    if (!subject || !session) return;
    await setSubjectStatus(subject, subject.status === 'photographed' ? 'not_photographed' : 'photographed', session.userId);
  }

  async function handleNext() {
    if (!next) return;
    if (autoMarkOnNext && subject && session && subject.status !== 'photographed') {
      await setSubjectStatus(subject, 'photographed', session.userId);
    }
    navigate(`/jobs/${jobId}/subjects/${next.id}`);
  }

  return (
    <div className={`qr-card-screen ${distractionFree ? 'qr-card-screen--distraction-free' : ''}`}>
      {!distractionFree && (
        <header className="qr-card-header">
          <Link to={`/jobs/${jobId}/groups/${subject.groupId}`} className="back-link">
            ← {group.name}
          </Link>
          <button className="btn btn--icon" onClick={() => setDistractionFree(true)} aria-label="Distraction-free mode">
            ⤢
          </button>
        </header>
      )}

      <div className="qr-card-body">
        <p className="qr-card-name">{subject.fullName}</p>
        <p className="qr-card-group">{group.name}</p>

        {blocked ? (
          <div className="qr-card-blocked" role="alert">
            <p>This subject has no valid QR payload from import.</p>
            <p>Do not photograph a placeholder. Fix this in Import Review, or re-import the job.</p>
          </div>
        ) : (
          <QRCanvas payload={subject.qrPayloadOriginal!} sizePx={Math.min(360, typeof window !== 'undefined' ? window.innerWidth - 48 : 320)} />
        )}

        <p className="qr-card-code">{subject.shortCode ?? '—'}</p>
      </div>

      <div className="qr-card-controls">
        <button className="btn btn--nav" disabled={!prev} onClick={() => prev && navigate(`/jobs/${jobId}/subjects/${prev.id}`)}>
          ← Prev
        </button>
        <button className={`btn btn--complete ${subject.status === 'photographed' ? 'btn--complete-active' : ''}`} onClick={handleMarkComplete}>
          {subject.status === 'photographed' ? '✓ Photographed' : 'Mark photographed'}
        </button>
        <button className="btn btn--nav" disabled={!next} onClick={handleNext}>
          Next →
        </button>
      </div>

      {distractionFree ? (
        <button className="qr-card-exit-df" onClick={() => setDistractionFree(false)} aria-label="Exit distraction-free mode">
          ×
        </button>
      ) : (
        <label className="qr-card-autoadvance">
          <input type="checkbox" checked={autoMarkOnNext} onChange={e => setAutoMarkOnNext(e.target.checked)} />
          Auto-mark photographed when moving to next
        </label>
      )}
    </div>
  );
}
